import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo, useRef } from 'react'
import { DIZEE_USER_STORAGE_EVENT, getActiveArtistIds } from '../lib/api'
import { useTheme } from '../context/ThemeContext'
import ArtistSwitcher from './ArtistSwitcher'
import { useUserPermissions } from '../hooks/useUserPermissions'
import { getConversationHistory, getLocalConversations } from '../lib/chatApi'
import { SidebarChatHistorySkeleton } from './ui/Skeleton'

type NavItemDef = { href: string; label: string; color: string }

const navItems: NavItemDef[] = [
  { href: '/dashboard', label: 'Dashboard', color: '#818cf8' },
  { href: '/activity', label: 'Activity', color: '#6366f1' },
  { href: '/schedule', label: 'Schedule', color: '#f59e0b' },
  { href: '/music', label: 'Music', color: '#1DB954' },
  { href: '/shows', label: 'Shows', color: '#9333ea' },
  { href: '/tour-manager', label: 'Touring', color: '#f97316' },
  { href: '/tasks', label: 'Tasks', color: '#FF6B35' },
  { href: '/money', label: 'Money', color: '#10b981' },
  { href: '/deals', label: 'Deals', color: '#ea580c' },
  { href: '/links', label: 'Links', color: '#0ea5e9' },
  { href: '/team', label: 'Team', color: '#8b5cf6' },
  { href: '/settings', label: 'Settings', color: '#64748b' },
  { href: '/knowledge', label: 'Guides', color: '#f59e0b' },
]

/** Gray icons for minimized sidebar only (stroke inherits currentColor). */
const navIcons: Record<string, JSX.Element> = {
  '/team': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  '/intelligence': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  '/activity': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  '/dashboard': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  '/schedule': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M6 21h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 16h6" />
    </svg>
  ),
  '/shows': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  ),
  '/tour-manager': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  ),
  '/tasks': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  '/music': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
    </svg>
  ),
  '/links': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  '/money': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  '/deals': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
    </svg>
  ),
  '/knowledge': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  '/settings': (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35A1.724 1.724 0 005.682 8.93c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
}

// ── Chat history helpers ─────────────────────────────────────────────
interface ConvSummary {
  _id: string
  conversationId?: string
  title?: string
  lastMessage?: string
  createdAt?: string
  updatedAt?: string
  messages?: Array<{ type?: string; role?: string; content: string }>
}

function getConvTitle(c: ConvSummary): string {
  if (c.title) return c.title
  if (c.lastMessage) return c.lastMessage.slice(0, 50)
  const first = c.messages?.find((m) => m.role === 'user' || m.type === 'user')
  if (first) return first.content.slice(0, 50)
  return 'New conversation'
}

function getDateGroup(dateStr?: string): string {
  if (!dateStr) return 'Older'
  const d = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86_400_000)
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000)
  if (d >= today) return 'Today'
  if (d >= yesterday) return 'Yesterday'
  if (d >= weekAgo) return 'This week'
  return 'Older'
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This week', 'Older']

// ── Component ────────────────────────────────────────────────────────
interface SidebarProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
}

export default function Sidebar({ isCollapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const { hasPermission } = useUserPermissions()
  
  const [user, setUser] = useState<any | null>(() => {
    try {
      const raw = localStorage.getItem('dizeeUser')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    const syncUserFromStorage = () => {
      try {
        const raw = localStorage.getItem('dizeeUser')
        setUser(raw ? JSON.parse(raw) : null)
      } catch {
        setUser(null)
      }
    }
    window.addEventListener(DIZEE_USER_STORAGE_EVENT, syncUserFromStorage)
    return () => window.removeEventListener(DIZEE_USER_STORAGE_EVENT, syncUserFromStorage)
  }, [])

  // ── Chat history state ──
  // Initialize from localStorage immediately so conversations show instantly
  const [chatConvs, setChatConvs] = useState<ConvSummary[]>(() => getLocalConversations() as any[])
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(false)
  const [chatExpanded, setChatExpanded] = useState(false)

  const onChatPage = location.pathname === '/chat'
  const activeConvId = onChatPage
    ? new URLSearchParams(location.search).get('id') || ''
    : ''

  // Auto-expand chat history when navigating to /chat, fetch if needed
  useEffect(() => {
    if (onChatPage) {
      setChatExpanded(true)
      if (!chatHistoryLoaded) fetchChatHistory()
    }
  }, [onChatPage, chatHistoryLoaded])

  // Fetch history when manually expanding (even if not on /chat)
  useEffect(() => {
    if (chatExpanded && !chatHistoryLoaded) {
      fetchChatHistory()
    }
  }, [chatExpanded, chatHistoryLoaded])

  // Listen for new-conversation events from ChatPage (same tab)
  useEffect(() => {
    const handler = () => {
      // Re-read from localStorage (instant) + async API merge
      setChatConvs(getLocalConversations() as any[])
      fetchChatHistory()
    }
    window.addEventListener('dizee:new-conversation', handler)
    return () => window.removeEventListener('dizee:new-conversation', handler)
  }, [])

  // Listen for localStorage changes from OTHER tabs (cross-tab sync)
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'dizee:chatConversations') {
        try {
          const updated = JSON.parse(e.newValue || '[]')
          setChatConvs(updated)
        } catch {
          /* ignore */
        }
      }
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  const fetchChatHistory = async () => {
    try {
      const r = await getConversationHistory()
      if (r.success && r.conversations) {
        setChatConvs(r.conversations)
      }
    } catch {
      /* silent */
    }
    setChatHistoryLoaded(true)
  }

  // Group conversations by date
  const grouped = chatConvs.reduce<Record<string, ConvSummary[]>>((acc, c) => {
    const g = getDateGroup(c.updatedAt || c.createdAt)
    ;(acc[g] ??= []).push(c)
    return acc
  }, {})

  // Re-read active artists when they change (e.g. toggled on Team Hub)
  const [activeArtistIds, setActiveArtistIds] = useState<string[]>(() => getActiveArtistIds())
  useEffect(() => {
    const sync = () => setActiveArtistIds(getActiveArtistIds())
    window.addEventListener('dizee:activeArtistsChanged', sync)
    return () => window.removeEventListener('dizee:activeArtistsChanged', sync)
  }, [])

  const isTeamMember = user?.professionRole && ['manager', 'support', 'promoter'].includes(user.professionRole)
  const isActingAsArtist = isTeamMember && activeArtistIds.length > 0

  // Map of route paths to permission section IDs
  const routeToSectionMap: Record<string, string> = {
    '/schedule': 'schedule',
    '/shows': 'shows',
    '/tour-manager': 'shows',
    '/tasks': 'tasks',
    '/music': 'music',
    '/links': 'links',
    '/knowledge': 'knowledge',
    '/team': 'team',
    '/settings': 'settings',
    '/activity': 'activity',
    '/money': 'finance',
    '/deals': 'deals',
    '/dashboard': 'activity',
  }

  // Filter nav items based on context and permissions
  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      // Hide Guides and Settings from the nav list (Settings is in the profile menu)
      if (item.href === '/knowledge' || item.href === '/settings') return false

      // Schedule is always visible for everyone
      if (item.href === '/schedule') return true

      // For non-team-members (artists), all items shown
      if (!isTeamMember) return true

      // ── Team member logic below ──
      // (Do not hide nav while permissions refetch after switching artists — stale permissions
      //  stay visible until the new snapshot loads so users can still switch artists and navigate.)

      // Chat, Team are always visible for team members
      if (item.href === '/chat' || item.href === '/team') return true

      // If team member is NOT acting as an artist, only show granted sections
      if (!isActingAsArtist) return false

      // Team member IS acting as artist — check permissions
      const sectionId = routeToSectionMap[item.href]
      if (sectionId) {
        return hasPermission(sectionId)
      }
      return true
    })
  }, [isTeamMember, isActingAsArtist, hasPermission])

  const isNavActive = (href: string) =>
    location.pathname === href ||
    (href === '/tour-manager' && location.pathname.startsWith('/tour-manager/'))

  const getInitials = (name: string) => {
    const parts = name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
    if (!parts.length) return 'U'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }

  const displayName =
    user?.name ||
    user?.username ||
    user?.email ||
    'Account'
  const avatarUrl = user?.avatar || user?.photo || user?.profile_image || null

  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [profileMenuOpen])

  const handleLogout = () => {
    // Persist active artist toggles per-user so they survive logout/login
    const currentIds = localStorage.getItem('dizee:activeArtistIds')
    if (currentIds) {
      if (user?.email) localStorage.setItem(`dizee:activeArtistIds:${user.email}`, currentIds)
      const uid = user?._id || user?.id
      if (uid) localStorage.setItem(`dizee:activeArtistIds:${uid}`, currentIds)
    }
    localStorage.removeItem('dizeeToken')
    localStorage.removeItem('dizeeUser')
    localStorage.removeItem('selectedArtist')
    localStorage.removeItem('dizee:activeArtistIds')
    localStorage.removeItem('dizee:artistCache')
    localStorage.removeItem('isNewSignup')
    setUser(null)
    setIsOpen(false)
    navigate('/login')
  }

  const renderNavItemLi = (item: NavItemDef) => {
    const isActive = isNavActive(item.href)
    const isChatItem = item.href === '/chat'

    if (isChatItem && !isCollapsed) {
      return (
        <li key={item.href}>
          <div
            className={`
              flex items-center justify-between
              py-2 px-2
              text-sm rounded-md transition-colors
              ${isActive
                ? 'text-black font-medium bg-gray-50 active-nav'
                : 'text-gray-600 hover:text-black hover:bg-gray-50/50'
              }
            `}
          >
            <Link to={item.href} onClick={() => setIsOpen(false)} className="flex-1 truncate">
              {item.label}
            </Link>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setChatExpanded(!chatExpanded)
              }}
              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-white/10 transition-colors ml-1"
              aria-label={chatExpanded ? 'Collapse chat history' : 'Expand chat history'}
            >
              <svg
                className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${chatExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          {chatExpanded && (
            <div className="mt-1 mb-2">
              <Link
                to="/chat"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-1.5 px-2 py-1.5 ml-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New chat
              </Link>
              {chatConvs.length > 0 && (
                <div className="mt-1 max-h-[45vh] overflow-y-auto">
                  {GROUP_ORDER.map((g) => {
                    const items = grouped[g]
                    if (!items?.length) return null
                    return (
                      <div key={g} className="mb-2">
                        <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 px-4 mb-0.5 uppercase tracking-wider">
                          {g}
                        </p>
                        {items.map((c) => {
                          const id = c._id || c.conversationId || ''
                          const active = id === activeConvId
                          return (
                            <Link
                              key={id}
                              to={`/chat?id=${id}`}
                              onClick={() => setIsOpen(false)}
                              className={`
                                block px-4 py-1.5 ml-2 text-xs truncate rounded-md transition-colors
                                ${
                                  active
                                    ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white font-medium'
                                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-700 dark:hover:text-gray-200'
                                }
                              `}
                            >
                              {getConvTitle(c)}
                            </Link>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}
              {!chatHistoryLoaded && <SidebarChatHistorySkeleton />}
            </div>
          )}
        </li>
      )
    }

    const collapsedLinkClass = `
      sidebar-tooltip-wrap relative text-sm rounded-md transition-colors touch-manipulation
      ${isActive
        ? 'text-black font-medium bg-gray-50 active-nav'
        : 'text-gray-600 hover:text-black hover:bg-gray-50/50'
      }
    `

    if (isCollapsed) {
      return (
        <li key={item.href}>
          <Link
            to={item.href}
            onClick={() => setIsOpen(false)}
            className={`${collapsedLinkClass} flex items-center justify-center py-2 px-0`}
            title={item.label}
            aria-label={item.label}
          >
            <span className="flex h-8 w-8 items-center justify-center text-gray-500 dark:text-gray-400 [&_svg]:block">
              {navIcons[item.href] ?? (
                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{item.label.charAt(0)}</span>
              )}
            </span>
            <span className="sidebar-tooltip">{item.label}</span>
          </Link>
        </li>
      )
    }

    const rowSurface = isActive
      ? 'bg-gray-50 active-nav dark:bg-white/[0.12]'
      : 'hover:bg-gray-50/50 dark:hover:bg-white/5'

    return (
      <li key={item.href}>
        <Link
          to={item.href}
          onClick={() => setIsOpen(false)}
          className={`
            sidebar-tooltip-wrap -ml-2 flex w-[calc(100%+0.5rem)] min-h-[40px] min-w-0 items-center truncate rounded-md pl-2 pr-2 text-sm touch-manipulation transition-colors
            ${rowSurface}
            ${isActive
              ? 'font-medium text-black dark:text-white'
              : 'text-gray-600 hover:text-black dark:text-gray-300 dark:hover:text-white'}
          `}
        >
          {item.label}
        </Link>
      </li>
    )
  }

  return (
    <>
      {/* Mobile menu button – hidden when sidebar is open (sidebar's own arrow closes it) */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed top-4 left-4 z-50 md:hidden p-2.5 rounded-lg bg-white/80 dark:bg-[rgb(var(--surface-rgb))]/80 backdrop-blur-sm border border-gray-200 dark:border-[rgb(var(--border-rgb))] shadow-sm"
          aria-label="Open menu"
        >
          <svg className="w-5 h-5 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed
          top-0 left-0
          h-dvh
          ${isCollapsed ? 'md:w-20 md:overflow-visible' : 'md:w-72'}
          w-72
          bg-white sidebar-shell
          z-40
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        style={{ transition: 'width 200ms ease-in-out, transform 200ms ease-in-out' }}
      >
        <div className="h-full flex flex-col">
          {/* Top section with logo and nav */}
          <div className={`flex-1 min-h-0 ${isCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
            <nav className={`${isCollapsed ? 'p-3' : 'pl-5 pr-6 pt-6 pb-6'}`}>
              <div
                className={`mb-8 flex h-8 w-full items-center ${isCollapsed ? 'justify-center' : 'justify-between gap-2'}`}
              >
                {!isCollapsed ? (
                  <>
                    <img src="/favicon.png" alt="DIZEE" className="h-8 w-8 shrink-0" />
                    <button
                      type="button"
                      onClick={() => {
                        // On mobile: close the sidebar drawer; on desktop: collapse it
                        if (window.innerWidth < 768) {
                          setIsOpen(false)
                        } else {
                          onToggleCollapse()
                        }
                      }}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                      aria-label="Minimize sidebar"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    aria-label="Expand sidebar"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Artist Switcher — full control when expanded; compact icon when sidebar is collapsed */}
              {isTeamMember && (
                <div className={isCollapsed ? 'mb-4 flex justify-center' : 'mb-6'}>
                  <ArtistSwitcher compact={isCollapsed} />
                </div>
              )}

              <div className="space-y-1">
                <ul className="space-y-1">
                  {visibleNavItems.map((item) => renderNavItemLi(item))}
                </ul>
              </div>
            </nav>
          </div>
          
          {/* Bottom section: theme toggle + profile */}
          <div
            ref={profileMenuRef}
            className={`flex-shrink-0 border-t border-gray-100 ${isCollapsed ? 'p-3 pt-3' : 'pl-5 pr-6 pt-3 pb-4'}`}
            style={{ paddingBottom: `max(${isCollapsed ? '0.75rem' : '1rem'}, calc(${isCollapsed ? '0.75rem' : '1rem'} + var(--sab, 0px)))` }}
          >
            {/* Theme toggle — always visible above profile */}
            <button
              onClick={toggleTheme}
              className={`
                w-full p-2 mb-2 rounded-md transition-colors
                ${isCollapsed ? 'flex justify-center' : 'flex items-center gap-2'}
                hover:bg-gray-50 dark:hover:bg-white/5
                text-gray-600 dark:text-gray-300
              `}
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
              {!isCollapsed && (
                <span className="text-sm">{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
              )}
            </button>

            {/* Clickable profile row */}
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className={`
                w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}
                py-2 rounded-md transition-colors
                hover:bg-gray-50 dark:hover:bg-white/5
              `}
              title={isCollapsed ? displayName : undefined}
              aria-label="Account menu"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="h-9 w-9 rounded-full object-cover border border-gray-200 shrink-0"
                />
              ) : (
                <div className="h-9 w-9 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold flex items-center justify-center border border-gray-200 shrink-0">
                  {getInitials(String(displayName))}
                </div>
              )}
              {!isCollapsed && (
                <>
                  <div className="min-w-0 text-left flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{displayName}</div>
                    <div className="text-xs text-gray-500 truncate">{user?.email || 'Signed in'}</div>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${profileMenuOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </>
              )}
            </button>

            {/* Inline expanded menu — smooth grid-rows transition */}
            <div
              className="grid transition-[grid-template-rows] duration-200 ease-out"
              style={{ gridTemplateRows: profileMenuOpen ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <div className={`mt-1 space-y-0.5 ${isCollapsed ? '' : 'pl-1'}`}>
                  <button
                    onClick={() => {
                      navigate('/settings')
                      setProfileMenuOpen(false)
                      setIsOpen(false)
                    }}
                    className={`
                      w-full flex items-center gap-2.5 py-2 rounded-md text-sm transition-colors
                      ${isCollapsed ? 'justify-center px-0' : 'px-2'}
                      text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-black dark:hover:text-white
                    `}
                    tabIndex={profileMenuOpen ? 0 : -1}
                    title={isCollapsed ? 'Settings' : undefined}
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35A1.724 1.724 0 005.682 8.93c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {!isCollapsed && <span>Settings</span>}
                  </button>
                  <button
                    onClick={() => {
                      setProfileMenuOpen(false)
                      handleLogout()
                    }}
                    className={`
                      w-full flex items-center gap-2.5 py-2 rounded-md text-sm transition-colors
                      ${isCollapsed ? 'justify-center px-0' : 'px-2'}
                      text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10
                    `}
                    tabIndex={profileMenuOpen ? 0 : -1}
                    title={isCollapsed ? 'Log out' : undefined}
                  >
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1" />
                    </svg>
                    {!isCollapsed && <span>Log out</span>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-10 z-30 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
